import { InfoButton } from 'components/buttons'
import { track } from 'lib/analytics/track'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import css from './HeroBanner.scss'
import {
  ShopLandingPageImageOverlay,
  ShopLandingPageLayout,
  ShopLandingPageTextColor,
} from '../CmsLandingPage.d'
import BannerText from '../BannerText'
import { useDynamicLinkTracking } from '../hooks'

type DisplayBreakpoint = 'Mobile' | 'Desktop' | undefined | null

export interface HeroBannerProps {
  backgroundImage?: string
  buttonText?: string
  buttonUrl?: string
  contentfulId?: string
  displayBreakpoint?: DisplayBreakpoint
  imageOverlay: ShopLandingPageImageOverlay
  layout?: ShopLandingPageLayout
  slot?: number
  subText?: string
  textColor?: ShopLandingPageTextColor
  title: string
}

const COMPONENT_TYPE = 'hero_banner'

const HeroBanner = ({
  backgroundImage,
  buttonText,
  buttonUrl,
  contentfulId,
  displayBreakpoint,
  imageOverlay = 'Transparent',
  layout = 'Middle - Center',
  slot = 1,
  subText,
  textColor = 'Dark',
  title,
}: HeroBannerProps) => {
  const [currentUser] = useCurrentUser()
  const registryProps = {
    isRegistryOwner: !!currentUser?.registryId,
    registryOwnerId: currentUser?.id,
    registryId: currentUser?.registryId,
  }

  const imageWidth = displayBreakpoint === 'Mobile' ? 1320 : 2640
  const styles = {
    backgroundImage: backgroundImage
      ? `url(${backgroundImage}?fm=jpg&fit=fill&w=${imageWidth}&q=60)`
      : undefined,
  }

  const baseEventPayload = {
    event: track.componentClicked,
    componentType: COMPONENT_TYPE,
    contentId: contentfulId,
    contentName: title,
    slotPosition: slot,
    ...registryProps,
  }

  const { tracker, handleLinkClicked } = useDynamicLinkTracking((link) => ({
    ...baseEventPayload,
    urlClicked: link.href,
  }))

  const handleButtonClicked = () =>
    tracker.trackEvent({
      ...baseEventPayload,
      urlClicked: buttonUrl,
    })

  return (
    <header
      className={css.ShopHero}
      data-testid="shopHeroContainer"
      style={styles}
    >
      <BannerText
        dataTestId="shopHeroContent"
        imageOverlay={imageOverlay}
        layout={layout}
        textColor={textColor}
      >
        <h1 className={css.Title} data-testid="shopHeroTitle">
          {title}
        </h1>
        {subText && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
          <h2
            className={css.Subtitle}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: subText || '' }}
            data-testid="shopHeroSubText"
            onClick={handleLinkClicked}
          />
        )}
        {buttonText && (
          <InfoButton
            pill
            data-testid="shopHeroButton"
            href={buttonUrl}
            onClick={handleButtonClicked}
          >
            {buttonText}
          </InfoButton>
        )}
      </BannerText>
    </header>
  )
}

export default HeroBanner
